import { useParams, Link, Navigate } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { posts, getPostBySlug, type BlogBlock } from '@/content/blog';
import { formatPostDate } from '@/content/format';
import { BlogCover } from '@/components/illustrations/BlogCover';
import { NotifyForm } from '@/components/NotifyForm';
import { useSeo } from '@/seo/useSeo';

function Block({ block }: { block: BlogBlock }): JSX.Element {
  switch (block.type) {
    case 'h2':
      return <h2 className="mt-10 font-display text-xl font-bold text-nexus-ink md:text-2xl">{block.text}</h2>;
    case 'ul':
      return (
        <ul className="mt-4 flex flex-col gap-2.5">
          {block.items.map((item) => (
            <li key={item} className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
              <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
              {item}
            </li>
          ))}
        </ul>
      );
    case 'quote':
      return (
        <blockquote className="my-8 border-l-2 border-nexus-purple/50 pl-5 text-lg italic text-white/80">
          {block.text}
        </blockquote>
      );
    case 'p':
    default:
      return <p className="mt-4 text-[15px] leading-relaxed text-white/75">{block.text}</p>;
  }
}

export function BlogPost(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const post = getPostBySlug(slug);

  useSeo({
    title: post ? post.title : 'Post not found',
    description: post ? post.excerpt : 'This article could not be found.',
    path: `/blog/${slug ?? ''}`,
    schema: post
      ? {
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.excerpt,
          datePublished: post.date,
          dateModified: post.date,
          author: { '@type': 'Organization', name: post.author, url: 'https://nexusrmm.ai/about' },
          publisher: { '@id': 'https://nexusrmm.ai/#organization' },
          image: 'https://nexusrmm.ai/og-image.png',
          mainEntityOfPage: `https://nexusrmm.ai/blog/${post.slug}`,
        }
      : undefined,
    article: post ? { publishedTime: post.date, authorUrl: 'https://nexusrmm.ai/about' } : undefined,
  });

  if (!post) return <Navigate to="/blog" replace />;

  const related = posts.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div>
      <article className="pt-16 pb-16 md:pt-24 md:pb-24">
        <div className="container-content max-w-[720px]">
          <p className="text-xs text-white/40">
            <Link to="/blog" className="text-white/40 no-underline hover:text-nexus-purple-light">
              Blog
            </Link>{' '}
            / {post.tag}
          </p>
          <div className="mt-4 flex items-center gap-2">
            <Chip
              label={post.tag}
              size="small"
              className="h-[22px] border border-nexus-purple/35 bg-transparent text-[10.5px] font-bold text-nexus-purple-light"
            />
            <span className="text-xs text-white/40">
              {formatPostDate(post.date)} · {post.readMinutes} min read · {post.author}
            </span>
          </div>
          <h1 className="mt-4 font-display text-3xl font-bold leading-[1.15] tracking-tight text-nexus-ink md:text-[40px]">
            {post.title}
          </h1>

          <div className="mt-8">
            <BlogCover cover={post.cover} size="lg" />
          </div>

          <div className="mt-2">
            {post.body.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </div>
        </div>
      </article>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">More from the blog</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-4 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
              >
                <BlogCover cover={p.cover} />
                <h3 className="mt-3 font-display text-[15px] font-semibold leading-snug text-nexus-ink">
                  {p.title}
                </h3>
                <p className="mt-1.5 flex-1 text-sm text-white/65">{p.excerpt}</p>
              </Link>
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Button component={Link} to="/blog" variant="outlined" endIcon={<ArrowForwardIcon />} className="border-white/15 normal-case">
              View all articles
            </Button>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
            <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
              Follow the build as it ships.
            </h2>
            <p className="mx-auto mt-3 max-w-[520px] text-white/70">
              Nexus is live in our own MSP operations and opening to a limited
              design-partner cohort. Join the private-preview list.
            </p>
            <div className="mt-8 flex justify-center">
              <NotifyForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
